import { api } from '@foundation/api';

/**
 * CSV upload service for campaigns.
 *
 * Flow: ask the API for a short-lived presigned S3 URL, then PUT the file
 * straight to S3 from the browser. The PUT deliberately bypasses the shared ky
 * client so the Cognito Bearer token is never sent to S3 — a presigned URL
 * carries its own authorisation, and extra auth headers break the signature.
 *
 * BACKEND NOT DEPLOYED YET: `POST /campaigns/{campaignId}/uploads` does not
 * exist in src/tower-adapter/router.ts, and no upload bucket exists in the CDK
 * stacks. Until both land, clicking Upload surfaces a plain-English failure.
 * Only requestCsvUploadUrl() below needs to change if the agreed contract
 * differs (for example presigned POST, or a route under /configs).
 */

/** Response contract expected from the presign endpoint. */
export interface PresignedUpload {
  /** Short-lived presigned S3 URL accepting a single PUT */
  uploadUrl: string;
  /** Key the object will land under, echoed back for confirmation + audit */
  objectKey: string;
}

/** Content type signed for and sent on the PUT. Both sides must agree exactly. */
export const CSV_CONTENT_TYPE = 'text/csv';

/** Client-side ceiling on CSV size. */
export const MAX_CSV_BYTES = 20 * 1024 * 1024;

/**
 * Upload destinations the backend accepts, mirroring the UPLOAD_PREFIXES keys in
 * the adapter handler (`input` → input/, `campaigns-input` → campaigns/input/).
 * The backend rejects anything else, so the UI is constrained to these two.
 */
export type UploadTarget = 'input' | 'campaigns-input';

/** Selectable destinations, in display order. */
export const UPLOAD_TARGETS: readonly UploadTarget[] = ['input', 'campaigns-input'];

/** Destination used when a campaign has not chosen one — matches the backend default. */
export const DEFAULT_UPLOAD_TARGET: UploadTarget = 'input';

/**
 * Validates a chosen file before any network call.
 * Returns a plain-English problem description, or null when the file is usable.
 */
export function validateCsvFile(file: File): string | null {
  const extension = file.name.split('.').pop()?.toLowerCase();

  if (extension !== 'csv') {
    return 'Only .csv files can be uploaded. Choose a file ending in .csv.';
  }

  if (file.size === 0) {
    return 'That file is empty. Choose a CSV file that contains data.';
  }

  if (file.size > MAX_CSV_BYTES) {
    const limitMb = Math.floor(MAX_CSV_BYTES / (1024 * 1024));
    return `That file is larger than the ${limitMb} MB limit. Split it into smaller files and upload them one at a time.`;
  }

  return null;
}

/**
 * Asks the API for a presigned URL to upload one CSV for a campaign.
 *
 * `target` selects the destination folder (see UploadTarget). It is omitted from
 * the request when unset, letting the backend apply its own default.
 */
export async function requestCsvUploadUrl(
  campaignId: string,
  file: File,
  target?: UploadTarget,
): Promise<PresignedUpload> {
  return api
    .post(`campaigns/${encodeURIComponent(campaignId)}/uploads`, {
      json: {
        fileName: file.name,
        contentType: CSV_CONTENT_TYPE,
        fileSize: file.size,
        ...(target ? { target } : {}),
      },
    })
    .json<PresignedUpload>();
}

/**
 * PUTs a file to a presigned S3 URL, reporting progress as a 0–100 percentage.
 * Uses XMLHttpRequest because fetch cannot report upload progress.
 */
export function putFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl, true);
    request.setRequestHeader('Content-Type', CSV_CONTENT_TYPE);
    request.setRequestHeader('x-amz-server-side-encryption', 'aws:kms');
    request.setRequestHeader('x-amz-server-side-encryption-aws-kms-key-id', import.meta.env.VITE_KMS_KEY_ARN);

    request.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };

    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress?.(100);
        resolve();
        return;
      }
      reject(new Error(`S3 upload failed with status ${request.status}`));
    };

    request.onerror = () => reject(new Error('Network error while uploading to S3'));
    request.onabort = () => reject(new Error('Upload was cancelled'));
    request.ontimeout = () => reject(new Error('Upload timed out'));

    request.send(file);
  });
}

/**
 * Full upload: presign, then send the file to S3.
 */
export async function uploadCampaignCsv(
  campaignId: string,
  file: File,
  onProgress?: (percent: number) => void,
  target?: UploadTarget,
): Promise<PresignedUpload> {
  const presigned = await requestCsvUploadUrl(campaignId, file, target);
  await putFileToPresignedUrl(presigned.uploadUrl, file, onProgress);
  return presigned;
}
